// Left panel — textarea, platform chips, analyse button, quick prediction
import React from 'react';
import {
    Box, Typography, TextField, Button, Chip, Divider,
    CircularProgress, LinearProgress, Avatar, Paper, alpha, useTheme,
} from '@mui/material';
import {
    TextFields as TextFieldsIcon,
    Bolt as BoltIcon,
    TrendingUp as TrendingUpIcon,
    CheckCircle as CheckCircleIcon,
    AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { ScoreRing } from './AnalyzePrimitives';
import { PLATFORMS, VIRALITY_COLORS } from './constants';

export default function InputPanel({
    text, onTextChange,
    selectedPlatform, onPlatformChange,
    onSubmit, loading, predLoading,
    prediction,
}) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const twitterLeft = text.length <= 280 ? 280 - text.length : null;
    const overTwitter = text.length > 280 ? text.length - 280 : null;
    const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;

    const scoreColor = prediction
        ? prediction.score >= 80 ? '#10b981' : prediction.score >= 60 ? '#f59e0b' : '#ef4444'
        : '#6366f1';

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                background: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 20px rgba(0,0,0,0.07)',
                display: 'flex',
                flexDirection: 'column',
                gap: 2.5,
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ bgcolor: alpha('#6366f1', 0.12), color: '#6366f1', width: 40, height: 40 }}>
                    <TextFieldsIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                        Your Content
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Paste or type your social media post
                    </Typography>
                </Box>
            </Box>

            <Divider />

            {/* Textarea */}
            <TextField
                fullWidth multiline rows={10}
                placeholder="Paste your social media post here…"
                value={text}
                onChange={e => onTextChange(e.target.value)}
                variant="outlined"
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 2.5,
                        fontSize: '0.95rem',
                        lineHeight: 1.7,
                        background: isDark ? alpha('#fff', 0.03) : alpha('#6366f1', 0.02),
                        '& fieldset': { borderColor: theme.palette.divider },
                        '&:hover fieldset': { borderColor: alpha('#6366f1', 0.5) },
                        '&.Mui-focused fieldset': { borderColor: '#6366f1', borderWidth: 2 },
                    },
                }}
            />

            {/* Counter row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: -1.5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {text.length} chars · {wordCount} words
                </Typography>
                {twitterLeft !== null && (
                    <Typography variant="caption" sx={{ color: alpha('#1DA1F2', 0.85), fontWeight: 600 }}>
                        {twitterLeft} left for Twitter
                    </Typography>
                )}
                {overTwitter !== null && (
                    <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 600 }}>
                        {overTwitter} over Twitter limit
                    </Typography>
                )}
            </Box>

            {/* Platform chips */}
            <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.7, fontSize: '0.68rem', mb: 1, display: 'block' }}>
                    Target Platform
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {PLATFORMS.map(p => (
                        <Chip
                            key={p.label}
                            label={p.label}
                            size="small"
                            onClick={() => onPlatformChange(prev => prev === p.label ? null : p.label)}
                            sx={{
                                fontWeight: 600, cursor: 'pointer',
                                bgcolor: selectedPlatform === p.label ? p.color : p.bg,
                                color: selectedPlatform === p.label ? 'white' : p.color,
                                border: `1px solid ${alpha(p.color, 0.3)}`,
                                transition: 'all 0.18s',
                                '&:hover': { bgcolor: p.color, color: 'white', transform: 'translateY(-1px)' },
                            }}
                        />
                    ))}
                </Box>
            </Box>

            {/* Submit button */}
            <Button
                fullWidth variant="contained" size="large"
                onClick={onSubmit}
                disabled={loading || predLoading || !text.trim()}
                startIcon={(loading || predLoading) ? <CircularProgress size={18} color="inherit" /> : <BoltIcon />}
                sx={{
                    borderRadius: 2.5, py: 1.7,
                    fontWeight: 800, fontSize: '1rem', letterSpacing: 0.4,
                    background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                    boxShadow: '0 6px 20px rgba(99,102,241,0.4)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #4338ca, #6d28d9)',
                        boxShadow: '0 8px 28px rgba(99,102,241,0.5)',
                        transform: 'translateY(-2px)',
                    },
                    '&:disabled': { background: alpha('#6366f1', 0.35), color: 'rgba(255,255,255,0.6)', boxShadow: 'none' },
                    transition: 'all 0.2s ease',
                }}
            >
                {(loading || predLoading) ? 'Analysing…' : 'Analyse Content'}
            </Button>

            {/* ── Quick Prediction (inline) ──────────────────────────────────────── */}
            {(prediction || predLoading) && (
                <Box>
                    <Divider sx={{ mb: 2.5 }}>
                        <Chip
                            label="Quick Prediction"
                            size="small"
                            icon={<AutoAwesomeIcon sx={{ fontSize: '13px !important' }} />}
                            sx={{ bgcolor: alpha('#6366f1', 0.08), color: '#6366f1', fontWeight: 600, fontSize: '0.72rem' }}
                        />
                    </Divider>

                    {predLoading ? (
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                            <CircularProgress size={32} sx={{ color: '#6366f1', mb: 1 }} />
                            <LinearProgress sx={{ borderRadius: 2, '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg,#6366f1,#8b5cf6)' } }} />
                        </Box>
                    ) : prediction && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            <ScoreRing value={prediction.score} max={100} color={scoreColor} size={80} label="Score" />
                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                                    <Chip
                                        label={`Virality: ${prediction.virality}`}
                                        size="small"
                                        sx={{
                                            fontWeight: 700, fontSize: '0.72rem',
                                            bgcolor: VIRALITY_COLORS[prediction.virality]?.bg,
                                            color: VIRALITY_COLORS[prediction.virality]?.text,
                                        }}
                                    />
                                    <Chip
                                        label={`Best: ${prediction.best}`}
                                        size="small"
                                        icon={<TrendingUpIcon sx={{ fontSize: '12px !important', color: 'white !important' }} />}
                                        sx={{ fontWeight: 700, fontSize: '0.72rem', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white' }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                    {prediction.suggestions.slice(0, 2).map((s, i) => (
                                        <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                            <CheckCircleIcon sx={{ color: '#6366f1', fontSize: 13, mt: 0.35, flexShrink: 0 }} />
                                            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5, fontSize: '0.75rem' }}>
                                                {s}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
        </Paper>
    );
}
